import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Button,
    Card,
    CardContent,
    RadioGroup,
    FormControlLabel,
    Radio,
    Drawer,
    Chip,
    OutlinedInput,
    Box,
    CircularProgress,
    Checkbox,
} from '@material-ui/core';
import {
    FilterList as FilterIcon,
    Search as SearchIcon,
    GetApp as DownloadIcon,
    Visibility as EyeIcon,
    Edit as EditIcon,
    Person as ProfileIcon,
    Close as CloseIcon,
    WhatsApp as WhatsAppIcon,
    Delete,
} from '@material-ui/icons';
import { formatDate, formatNumber, routes } from '../../constants.js/misc';
import FilterModal from './OrderPageMobileFilter';
import useStyles from './OrderPageMobile.styles';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    deleteAllOrders,
    downloadOrders,
    getOrdersService,
    updateOrder,
} from '../../services/orders';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { errorToast, infoToast, successToast } from '../../constants.js/misc';
import { setOrderToEdit, setPageState, setRowsPerPageState } from '../../modules/orders';
import { debounce } from 'lodash';
import { paymentStatusConfigs, shipmentStatusConfigs } from '../../constants.js/orders';
import { getOrderOrigin } from '../../utils/orderOrigin';
import ConfirmableButton from '../../components/ConfirmableButton';

export const OrderPageMobile = ({
    storeId,
    initialRowsPerPage,
    initialPage,
    storeData,
    setOrderToEdit,
    isPlanNearExpiry,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const classes = useStyles();
    const history = useHistory();

    const [orders, setOrders] = useState([]);
    const [isPaymentStatusDrawerOpen, setIsPaymentStatusDrawerOpen] = useState(false);
    const [isShipmentStatusDrawerOpen, setIsShipmentStatusDrawerOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
    const [selectedShipmentStatus, setSelectedShipmentStatus] = useState('');
    const [_newPaymentStatusText, setNewPaymentStatusText] = useState('');
    const [_newShipmentStatusText, setNewShipmentStatusText] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selected, setSelected] = useState([]);

    const [filters, setFilters] = useState({
        datePreset: '',
        dateFrom: '',
        dateTo: '',
        branch: '',
        orderStatus: [],
        deliveryStatus: [],
        salesChannelState: {
            whatsapp: false,
            webstore: false,
            phone: false,
            inperson: false,
        },
    });

    const [page, setPage] = useState(initialPage || 0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [isSearchEnabled, setIsSearchEnabled] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Status handling functions
    const handlePaymentStatusClick = (order) => {
        setSelectedOrder(order);
        setSelectedPaymentStatus(order.order_status.name.toString().toLowerCase());
        setIsPaymentStatusDrawerOpen(true);
    };

    const handleShipmentStatusClick = (order) => {
        setSelectedOrder(order);
        setSelectedShipmentStatus(
            order.shipping_order_status.name.toString().replace(' ', '').toLowerCase()
        );
        setIsShipmentStatusDrawerOpen(true);
    };

    const handlePaymentStatusChange = (event) => {
        setSelectedPaymentStatus(event.target.value);
    };

    const handleShipmentStatusChange = (event) => {
        setSelectedShipmentStatus(event.target.value);
    };

    const handleStatusSave = () => {
        const orderToUpdate = orders.find((order) => order.id === selectedOrder.id);
        if (selectedPaymentStatus) {
            setNewPaymentStatusText(paymentStatusConfigs[selectedPaymentStatus]?.label);
            setIsPaymentStatusDrawerOpen(false);

            setOrders((prevOrders) =>
                prevOrders.map((order) =>
                    order.id === selectedOrder.id
                        ? { ...order, paymentStatus: selectedPaymentStatus }
                        : order
                )
            );
            return updateOrder(storeId, selectedOrder.id, {
                ...orderToUpdate,
                user_store_id: orderToUpdate.user_store_id,
                order_status_id: paymentStatusConfigs[selectedPaymentStatus]?.value,
            })
                .then(() => {
                    setTimeout(() => {
                        enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
                    }, 1500);
                    fetchOrders(storeId, 1, searchQuery, filters, false);
                })
                .catch(() => {
                    setTimeout(() => {
                        enqueueSnackbar(t('OrderDetail.submitError'), errorToast);
                    }, 1500);
                });
        }

        if (selectedShipmentStatus) {
            setNewShipmentStatusText(shipmentStatusConfigs[selectedShipmentStatus]?.label);
            setIsShipmentStatusDrawerOpen(false);

            setOrders((prevOrders) =>
                prevOrders.map((order) =>
                    order.id === selectedOrder.id
                        ? { ...order, shipmentStatus: selectedShipmentStatus }
                        : order
                )
            );
            return updateOrder(storeId, selectedOrder.id, {
                ...orderToUpdate,
                user_store_id: orderToUpdate.user_store_id,
                shipping_order_status_id: shipmentStatusConfigs[selectedShipmentStatus]?.value,
            })
                .then(() => {
                    setTimeout(() => {
                        enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
                    }, 1500);
                    fetchOrders(storeId, 1, searchQuery, filters, false);
                })
                .catch(() => {
                    setTimeout(() => {
                        enqueueSnackbar(t('OrderDetail.submitError'), errorToast);
                    }, 1500);
                });
        }
    };


    const getShippingFromOrder = () => {
        if (!storeData?.delivery_cost_enabled) return 0;
        const raw = storeData?.delivery_cost_details;
        const num = Number(raw);
        return Number.isNaN(num) ? 0 : Math.max(0, num);
    };

    const getOrderSubtotal = (order) => {
        const details = Array.isArray(order?.order_details) ? order.order_details : [];
        return details.reduce((acc, detail) => {
            const price = detail?.price ?? detail?.product?.discount_price ?? detail?.product?.price ?? 0;
            const qty = Number(detail?.quantity) || 0;
            return acc + qty * Number(price);
        }, 0);
    };

    const getApiTotalFromOrder = (order) => {
        const apiTotal =
            order?.total ??
            order?.order_total ??
            order?.grand_total ??
            order?.total_amount ??
            order?.amount;
        const num = Number(apiTotal);
        return Number.isNaN(num) ? null : Math.max(0, num);
    };

    const getCouponDiscountFromRow = (order) => {
        const subtotal = getOrderSubtotal(order);
        const shipping = getShippingFromOrder();
        const apiTotal = getApiTotalFromOrder(order);

        if (apiTotal != null) {
            const diff = subtotal + shipping - apiTotal;
            const numDiff = Number(diff);
            if (!Number.isNaN(numDiff) && numDiff > 0) {
                return numDiff;
            }
        }

        const raw =
            order?.coupon_discount_amount ??
            order?.coupon_discount ??
            order?.coupon?.discount_amount ??
            order?.applied_coupon?.discount_amount ??
            0;
        return Math.max(0, Number(raw) || 0);
    };

    const getOrderTotal = (order) => {
        const apiTotal = getApiTotalFromOrder(order);
        if (apiTotal != null) {
            return apiTotal;
        }
        const subtotal = getOrderSubtotal(order);
        const shipping = getShippingFromOrder();
        const couponDiscount = getCouponDiscountFromRow(order);
        return Math.max(0, subtotal + shipping - couponDiscount);
    };

    const handleDownloadOrders = () => {
        downloadOrders(storeId);
        return;
    };

    const handleApplyFilters = (newFilters) => {
        setFilters(newFilters);
        setOrders([]);
        setPage(page);
        setHasMore(true);
        setLoading(true);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            datePreset: '',
            dateFrom: '',
            dateTo: '',
            branch: '',
            orderStatus: [],
            deliveryStatus: [],
            salesChannelState: {
                whatsapp: false,
                webstore: false,
                phone: false,
                inperson: false,
            },
        };

        setFilters(clearedFilters);
        setOrders([]);
        setPage(page);
        setHasMore(true);
        setLoading(true);
    };

    const handleRemoveFilter = (type, item) => {
        let updatedFilters = { ...filters };

        if (type === 'order') {
            updatedFilters.orderStatus = filters.orderStatus.filter(
                (s) => s.status_id !== item.status_id
            );
        }

        if (type === 'delivery') {
            updatedFilters.deliveryStatus = filters.deliveryStatus.filter(
                (s) => s.status_id !== item.status_id
            );
        }

        if (type === 'branch') {
            updatedFilters.branch = '';
        }

        setFilters(updatedFilters);
        setOrders([]);
        setPage(page);
        setHasMore(true);
        setLoading(true);
    };

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((query) => {
            setOrders([]);
            setPage(page);
            setLoading(true);
        }, 500),
        []
    );

    // Handle search query changes
    useEffect(() => {
        if (searchQuery !== '') {
            debouncedSearch(searchQuery);
        } else if (searchQuery === '') {
            // Reset when search is cleared
            setOrders([]);
            setPage(page);
            setLoading(true);
        }
    }, [searchQuery, debouncedSearch]);

    // Main data fetching function
    const fetchOrders = useCallback(
        debounce((storeId, pageNum, searchQuery, filterParams, append = false) => {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const {
                dateFrom = '',
                dateTo = '',
                deliveryStatus = [],
                branch = '',
                orderStatus = [],
            } = filterParams || {};

            const parsedDate = dateFrom && dateTo ? `${dateFrom},${dateTo}` : '';

            getOrdersService(
                storeId,
                10,
                pageNum,
                parsedDate,
                branch,
                orderStatus?.map((status) => status.status_id) || null,
                deliveryStatus?.map((status) => status.status_id) || null,
                '',
                searchQuery,
                '',
                ''
            )
                .then(({ data }) => {
                    if (append) {
                        setOrders((prevOrders) => [...prevOrders, ...data]);
                        setLoadingMore(false);
                    } else {
                        setOrders(data);
                        setLoading(false);
                    }
                    setHasMore(data.length === 10);
                })
                .catch(() => {
                    setLoading(false);
                    setLoadingMore(false);
                });
        }, 300),
        []
    );

    useEffect(() => {
        if (storeId) {
            // Only fetch for the current page when page changes, not reset page here
            fetchOrders(storeId, page, searchQuery, filters);
        }
    }, [storeId, filters, page, searchQuery, fetchOrders]);

    // Load more orders for pagination
    // Load more data on button click (infinite scroll style, but manual)
    const handleGetMoreData = () => {
        if (!loadingMore && hasMore && !loading) {
            fetchOrders(storeId, page + 1, searchQuery, filters, true);
            setPage((prevPage) => prevPage + 1);
        }
    };

    const handleBulkDelete = async () => {
        try {
            await deleteAllOrders(storeId, selected);
            localStorage.removeItem('selectedOrders');
            setSelected([]);
        } catch (err) {
            console.error('Bulk delete failed', err);
        } finally {
            enqueueSnackbar('Eliminación exitosa', successToast);
            window.location.reload();
        }
    };

    const handleSelectOrder = (evt, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        localStorage.setItem('selectedOrders', JSON.stringify(newSelected));
        setSelected(newSelected);
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;
    const isDeleteBulkActionDisabled = selected.length === 0;

    useEffect(() => {
        const selectedOrders = localStorage.getItem('selectedOrders');
        setSelected(selectedOrders ? JSON.parse(selectedOrders) : []);
    }, [setSelected]);

    // HOCs - Status chip rendering functions
    const getPaymentStatusChip = (status) => {
        const config = paymentStatusConfigs[status.toString().toLowerCase()];
        if (config) {
            return (
                <Chip
                    label={config.label}
                    className={`${classes.statusChip} ${classes[config.class]}`}
                    size="small"
                />
            );
        }
    };

    const getShipmentStatusChip = (status) => {
        const config = shipmentStatusConfigs[status.toString().toLowerCase()];
        if (config) {
            return (
                <Chip
                    label={config.label}
                    className={`${classes.statusChip} ${classes[config.class]}`}
                    size="small"
                />
            );
        }
    };

    const renderPaymentStatusOptions = () => {
        return Object.entries(paymentStatusConfigs).map(([value, config]) => (
            <FormControlLabel
                key={value}
                value={value}
                control={<Radio color="primary" />}
                label={
                    <div className={classes.statusOptionContent}>
                        {getPaymentStatusChip(value)}
                        <Typography variant="body2" color="textSecondary">
                            {config.description}
                        </Typography>
                    </div>
                }
                className={classes.statusOption}
            />
        ));
    };

    const renderShipmentStatusOptions = () => {
        return Object.entries(shipmentStatusConfigs).map(([value, config]) => (
            <FormControlLabel
                key={value}
                value={value}
                control={<Radio color="primary" />}
                label={
                    <div className={classes.statusOptionContent}>
                        {getShipmentStatusChip(value)}
                        <Typography variant="body2" color="textSecondary">
                            {config.description}
                        </Typography>
                    </div>
                }
                className={classes.statusOption}
            />
        ));
    };

    return (
        <div className={classes.root}>
            {/* App Bar */}
            <AppBar
                position="relative"
                className={{
                    [classes.lowerAppBar]: isPlanNearExpiry,
                    [classes.appBar]: !isPlanNearExpiry,
                }}
            >
                <Toolbar className={classes.toolbar}>
                    <Typography variant="h6" className={classes.title}>
                        Pedidos
                    </Typography>
                    {isSearchEnabled ? (
                        <>
                            <OutlinedInput
                                placeholder="Buscar pedidos"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={classes.searchInput}
                            />
                            <IconButton
                                onClick={() => {
                                    setIsSearchEnabled(false);
                                    setSearchQuery('');
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </>
                    ) : (
                        <div className={classes.headerButtons}>
                            <IconButton
                                className={classes.iconButton}
                                size="small"
                                onClick={() => setIsFilterOpen(true)}
                            >
                                <FilterIcon />
                            </IconButton>
                            <IconButton
                                className={classes.iconButton}
                                size="small"
                                onClick={() => setIsSearchEnabled(true)}
                            >
                                <SearchIcon />
                            </IconButton>
                            <Button
                                className={classes.downloadButton}
                                startIcon={<DownloadIcon />}
                                size="small"
                                onClick={handleDownloadOrders}
                            >
                                Descargar
                            </Button>
                        </div>
                    )}
                </Toolbar>
            </AppBar>

            {/* Active Filters Chips */}
            {(filters.orderStatus.length > 0 ||
                filters.deliveryStatus.length > 0 ||
                filters.branch) && (
                <Box className={classes.activeFiltersContainer}>
                    <Box className={classes.activeFiltersChips}>
                        {filters.orderStatus.map((s) => (
                            <Chip
                                key={`order-${s.status_id}`}
                                label={s.status_name}
                                onDelete={() => handleRemoveFilter('order', s)}
                                className={classes.activeFilterChip}
                                variant="outlined"
                                size="small"
                            />
                        ))}
                        {filters.deliveryStatus.map((s) => (
                            <Chip
                                key={`delivery-${s.status_id}`}
                                label={s.status_name}
                                onDelete={() => handleRemoveFilter('delivery', s)}
                                className={classes.activeFilterChip}
                                variant="outlined"
                                size="small"
                            />
                        ))}
                        {filters.branch && (
                            <Chip
                                key={`branch-${filters.branch}`}
                                label={`Sucursal: ${filters.branch}`}
                                onDelete={() => handleRemoveFilter('branch')}
                                className={classes.activeFilterChip}
                                variant="outlined"
                                size="small"
                            />
                        )}
                    </Box>
                </Box>
            )}

            {/* Main Content */}
            <div className={classes.mainContent}>
                <div>
                    <ConfirmableButton
                        className={`${classes.deleteButton}`}
                        variant="contained"
                        fullWidth={false}
                        startIcon={<Delete />}
                        confirmMessage="¿Seguro que desea eliminar los pedidos seleccionados? Esta acción no se puede deshacer."
                        confirmButtonText="Eliminar"
                        cancelButtonText="Cancelar"
                        disabled={isDeleteBulkActionDisabled}
                        onClick={handleBulkDelete}
                    >
                        Eliminar pedidos
                    </ConfirmableButton>
                </div>
                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <CircularProgress style={{ marginBottom: '20px', color: '#9EE870' }} />
                        <Typography>Cargando pedidos...</Typography>
                    </div>
                ) : orders.length > 0 ? (
                    orders.map((order) => (
                        <Card className={classes.orderCard} key={order.id}>
                            <CardContent>
                                <div className={classes.cardHeader}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={isSelected(order.id)}
                                                onChange={(event) =>
                                                    handleSelectOrder(event, order.id)
                                                }
                                            />
                                            <span className={classes.orderNumber}>
                                                #{order.sequence}
                                            </span>
                                            <span className={classes.orderDate}>
                                                {formatDate(order.created_at)}
                                            </span>
                                        </div>
                                        <div className={classes.orderAmount}>
                                            {formatNumber(getOrderTotal(order), storeData?.currency)}
                                        </div>
                                    </div>
                                    <div onClick={() => handlePaymentStatusClick(order)}>
                                        {getPaymentStatusChip(order.order_status.name)}
                                    </div>
                                </div>

                                <div className={classes.orderDetails}>
                                    · {(order?.order_details?.length ?? 0)} productos ·{' '}
                                    <Link
                                        to={routes.ordersPage + '/' + order.id}
                                        onClick={() => setOrderToEdit(order)}
                                    >
                                        <span style={{ color: '#173300', fontWeight: 500 }}>
                                            Ver detalle
                                        </span>
                                    </Link>
                                </div>

                                <div className={classes.customerInfo}>
                                    <ProfileIcon className={classes.customerIcon} />
                                    <span>
                                        {order?.customer?.first_name ?? ''} ·{' '}
                                        {(() => {
                                            const origin = getOrderOrigin(order);
                                            return `${origin.channelLabel} · ${origin.primaryLabel}`;
                                        })()}
                                    </span>
                                </div>

                                <div className={classes.cardActions}>
                                    <Link to={routes.ordersPage + '/' + order.id}>
                                        <Button
                                            className={`${classes.actionButton} ${classes.viewButton}`}
                                            startIcon={<EyeIcon />}
                                            size="small"
                                            onClick={() => setOrderToEdit(order)}
                                        >
                                            Ver
                                        </Button>
                                    </Link>
                                    <a
                                        href={`https://wa.me/${order?.customer?.phone_number}`}
                                        style={{ textDecoration: 'none' }}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Button
                                            className={`${classes.actionButton} ${classes.whatsappButton}`}
                                            startIcon={
                                                <WhatsAppIcon style={{ color: '#ffffff' }} />
                                            }
                                            size="small"
                                        >
                                            WhatsApp
                                        </Button>
                                    </a>
                                    <Button
                                        className={`${classes.actionButton} ${classes.statusButton}`}
                                        startIcon={<EditIcon />}
                                        size="small"
                                        onClick={() => handleShipmentStatusClick(order)}
                                    >
                                        {order.shipping_order_status.name}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        // </Link>
                    ))
                ) : (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <Typography>No se encontraron pedidos</Typography>
                    </div>
                )}

                {/* Load More Button */}
                {hasMore && !loading && (
                    <div
                        style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}
                    >
                        <Button
                            className={`${classes.actionButton} ${classes.statusButton}`}
                            size="small"
                            onClick={handleGetMoreData}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Cargando...' : 'Cargar más'}
                        </Button>
                    </div>
                )}
            </div>

            {/* Payment Status Change Dialog */}
            <Drawer
                anchor="bottom"
                open={isPaymentStatusDrawerOpen}
                onClose={() => setIsPaymentStatusDrawerOpen(false)}
                PaperProps={{ style: { borderRadius: '16px 16px 0px 0px' } }}
            >
                <div className={classes.dialogTitle}>
                    <Typography variant="h6">Cambiar estado</Typography>
                    <IconButton onClick={() => setIsPaymentStatusDrawerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div>
                    <RadioGroup value={selectedPaymentStatus} onChange={handlePaymentStatusChange}>
                        {renderPaymentStatusOptions()}
                    </RadioGroup>
                </div>
                <div
                    style={{
                        padding: '16px 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        onClick={() => setIsPaymentStatusDrawerOpen(false)}
                        style={{ color: '#4A4A4A', flex: 1 }}
                        fullWidth
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleStatusSave}
                        style={{ backgroundColor: '#9EE870', color: 'white', flex: 1 }}
                        fullWidth
                    >
                        Guardar
                    </Button>
                </div>
            </Drawer>

            {/* Shipment Status Change Dialog */}
            <Drawer
                anchor="bottom"
                open={isShipmentStatusDrawerOpen}
                onClose={() => setIsShipmentStatusDrawerOpen(false)}
                PaperProps={{ style: { borderRadius: '16px 16px 0px 0px' } }}
            >
                <div className={classes.dialogTitle}>
                    <Typography variant="h6">Cambiar estado</Typography>
                    <IconButton onClick={() => setIsShipmentStatusDrawerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div>
                    <RadioGroup
                        value={selectedShipmentStatus}
                        onChange={handleShipmentStatusChange}
                    >
                        {renderShipmentStatusOptions()}
                    </RadioGroup>
                </div>
                <div
                    style={{
                        padding: '16px 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        onClick={() => setIsShipmentStatusDrawerOpen(false)}
                        style={{ color: '#4A4A4A', flex: 1 }}
                        fullWidth
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleStatusSave}
                        style={{ backgroundColor: '#9EE870', color: 'white', flex: 1 }}
                        fullWidth
                    >
                        Guardar
                    </Button>
                </div>
            </Drawer>

            {/* Filter Modal */}
            <FilterModal
                open={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                currentFilters={filters}
                storeId={storeId}
            />
        </div>
    );
};

const mapStateToProps = ({ auth, orders, userStores, common }) => ({
    storeId: auth.storeId,
    initialPage: orders.page,
    isPlanNearExpiry: userStores.isPlanNearExpiry,
    storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setOrderToEdit,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(OrderPageMobile);
